import { useCallback, useRef } from 'react'
import { Pressable, Text } from 'react-native'
import Constants from 'expo-constants'
import { useDeveloperMode } from '@/src/hooks/useDeveloperMode'
import { useToast } from '@/src/hooks/useToast'
import { toggleDeveloperMode } from '@/src/services/developer-mode'
import { useTranslation } from 'react-i18next'

import { body3 } from '../../../theme/typography'
import { settingsFooterVersionTextStyle } from './SettingsPrimitives'

const REQUIRED_VERSION_TAPS = 10
const VERSION_TAP_WINDOW_MS = 3_000

export function SettingsVersionFooter() {
  const { t } = useTranslation(['profile', 'common'])
  const { showToast } = useToast()
  const isDeveloperModeEnabled = useDeveloperMode()
  const recentTapTimes = useRef<number[]>([])
  const isTogglePending = useRef(false)

  const handleVersionPress = useCallback(() => {
    const now = Date.now()
    recentTapTimes.current = recentTapTimes.current.filter(
      (tapTime) => now - tapTime <= VERSION_TAP_WINDOW_MS,
    )
    recentTapTimes.current.push(now)

    if (recentTapTimes.current.length < REQUIRED_VERSION_TAPS) return

    recentTapTimes.current = []
    if (isTogglePending.current) return

    isTogglePending.current = true
    void toggleDeveloperMode()
      .then((enabled) => {
        showToast({
          title: enabled
            ? t('profile:developer_mode_enabled_title')
            : t('profile:developer_mode_disabled_title'),
          message: enabled
            ? t('profile:developer_mode_enabled_message')
            : t('profile:developer_mode_disabled_message'),
          variant: 'success',
        })
      })
      .catch(() => {
        showToast({
          title: t('common:error'),
          message: t('profile:developer_mode_update_failed'),
          variant: 'error',
        })
      })
      .finally(() => {
        isTogglePending.current = false
      })
  }, [showToast, t])

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('profile:version_accessibility_label', {
        version: Constants.expoConfig?.version ?? '1.0.0',
      })}
      hitSlop={12}
      onPress={handleVersionPress}
    >
      <Text style={[body3, settingsFooterVersionTextStyle]}>
        {t('profile:version_label')} {Constants.expoConfig?.version ?? '1.0.0'}
        {isDeveloperModeEnabled
          ? ` · ${t('profile:developer_mode_label')}`
          : ''}
      </Text>
    </Pressable>
  )
}
